import React, {FC, memo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {AsyncStatusesEnum} from '../../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {AttachmentModel} from '../../../../../../libs/shared/core/src/models/api/attachmentModel';
import {AsyncData} from '../../../../../../libs/shared/ui/src/components/loaders/asyncDataHelpers';
import {AsyncLoader} from '../../../../../../libs/shared/ui/src/components/loaders/asyncLoader';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {MessageAttachment} from '../../activity/attachments/messageAttachment';
import {StyledEmptyDiv, StyledLoaderDiv} from './conversationSummaryCommonStyles';
import {ConversationSummarySectionsEnum} from './conversationSummarySections';

/*
 * Props.
 */

interface ConversationSummaryAttachmentsProps {
  attachments: AsyncData<ReadonlyArray<AttachmentModel>>;
  onAttachmentsRetryClick: () => void;
}

/*
 * Style.
 */

const StyledAttachmentsDiv = styled.div`
  margin-top: 4px;
  margin-right: -11px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledNoAttachmentsDiv = styled(StyledEmptyDiv)`
  padding-left: 4px;
`;

/*
 * Component.
 */

export const ConversationSummaryAttachments: FC<ConversationSummaryAttachmentsProps> = memo((props) => (
  <StyledAttachmentsDiv data-testid={ConversationSummarySectionsEnum.ATTACHMENTS}>
    {renderAttachmentsContent(props.attachments, props.onAttachmentsRetryClick)}
  </StyledAttachmentsDiv>
));

function renderAttachmentsContent(
  attachments: AsyncData<ReadonlyArray<AttachmentModel>>,
  onAttachmentsRetryClick: () => void,
) {
  if (attachments.status !== AsyncStatusesEnum.LOADED) {
    return renderLoader(attachments.status, onAttachmentsRetryClick);
  }

  if (attachments.data.length === 0) {
    return renderNoAttachmentsMessage();
  }

  return renderMessageAttachments(attachments.data);
}

function renderLoader(attachmentsLoadingStatus: AsyncStatusesEnum, onAttachmentsRetryClick: () => void) {
  return (
    <StyledLoaderDiv>
      <AsyncLoader
        onRetryClick={onAttachmentsRetryClick}
        size={VisualSizesEnum.SMALL}
        status={attachmentsLoadingStatus}
      />
    </StyledLoaderDiv>
  );
}

function renderNoAttachmentsMessage() {
  return (
    <StyledNoAttachmentsDiv data-testid="conversationSummaryNoAttachments">
      <Trans id="conversation-summary-attachments-none">None</Trans>
    </StyledNoAttachmentsDiv>
  );
}

function renderMessageAttachments(attachmentModels: ReadonlyArray<AttachmentModel>) {
  return attachmentModels.map((attachmentModel) => (
    <MessageAttachment
      key={attachmentModel.uid}
      attachment={attachmentModel}
      hasLargeAttachmentView={false}
    />
  ));
}
